import { Icon } from '@iconify/react';
import {
  Box,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

export default function ChatInput({
  value,
  onChange,
  onSend,
  onKeyDown,
  isPending,
  isDeepSearchMode,
  onToggleDeepSearch,
  hasTranscriptError,
  variant = 'bottom',
}) {
  const ToggleAdornment = (
    <InputAdornment position="start" sx={{ position: 'absolute', left: 12, bottom: 20 }}>
      <Switch
        size="small"
        checked={isDeepSearchMode}
        onChange={onToggleDeepSearch}
        color="secondary"
      />
      <Typography variant="caption" ml={0.5} whiteSpace="nowrap">
        Enable Deep Search
      </Typography>
      <Tooltip
        title="Deep Search goes through every conversation in your transcripts and answers your question by checking each speaker's response—so you get a complete, accurate answer without missing anything important."
        placement="bottom"
      >
        <Icon
          icon="mdi:information-outline"
          width={16}
          height={16}
          style={{ marginLeft: 4, cursor: 'pointer' }}
        />
      </Tooltip>
    </InputAdornment>
  );

  const SendAdornment = (
    <InputAdornment position="end" sx={{ pr: 1 }}>
      <IconButton
        onClick={onSend}
        disabled={isPending}
        sx={{ backgroundColor: '#7d59e8', '&:hover': { backgroundColor: '#7d59e8' } }}
      >
        <Icon icon="tabler:send" width={24} height={24} color="#fff" />
      </IconButton>
    </InputAdornment>
  );

  const baseInputProps = {
    variant: 'outlined',
    fullWidth: true,
    onKeyDown,
    InputProps: { startAdornment: ToggleAdornment, endAdornment: SendAdornment },
  };

  const centeredSx = {
    width: 'min(640px, 90vw)',
    '& .MuiOutlinedInput-root': {
      py: 1,
      height: 120,
      bgcolor: '#fff',
      borderRadius: '24px',
      border: '1px solid #d9d9d9',
      fontSize: '1rem',
      transition: 'box-shadow 150ms ease',
      '&:hover': { boxShadow: '0 0 0 2px rgba(125,89,232,0.2)' },
      '&.Mui-focused fieldset': { borderColor: '#7d59e8' },
    },
  };

  const bottomSx = {
    width: '80%',
    '& .MuiOutlinedInput-root': {
      py: 1,
      height: 96,
      bgcolor: 'rgba(255,255,255,0.47)',
      backdropFilter: 'blur(11px) saturate(101%)',
      WebkitBackdropFilter: 'blur(11px) saturate(101%)',
      borderRadius: '24px',
      border: '1px solid rgba(209,213,219,0.3)',
      fontSize: '1rem',
      transition: 'box-shadow 150ms ease',
      '&:hover': { boxShadow: '0 0 0 2px rgba(125,89,232,0.2)' },
      '&.Mui-focused fieldset': { borderColor: '#7d59e8' },
      '& .MuiOutlinedInput-input': {
        padding: '16.5px 14px',
        boxSizing: 'border-box',
      },
    },
  };

  if (variant === 'centered') {
    return (
      <TextField
        {...baseInputProps}
        placeholder={
          hasTranscriptError ? 'Add transcripts to start chatting...' : 'Ask anything...'
        }
        value={value}
        onChange={onChange}
        disabled={hasTranscriptError}
        sx={centeredSx}
      />
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={1}
      position="absolute"
      bottom={10}
      width="96%"
    >
      <TextField
        {...baseInputProps}
        placeholder={
          hasTranscriptError
            ? 'Add transcripts to start chatting...'
            : 'Ask questions about your data...'
        }
        value={value}
        onChange={onChange}
        disabled={hasTranscriptError}
        sx={bottomSx}
      />
    </Box>
  );
}
